import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  AddRounded,
  SearchRounded,
  VisibilityRounded,
  DeleteRounded,
  HistoryEduRounded,
  EditOutlined,
  PersonAddRounded,
  FilterListRounded,
  ClearRounded,
} from '@mui/icons-material';
import { CreateAdmissionDialog } from '../sis/components/CreateAdmissionDialog';
import { TYPE_COLORS } from '../sis/components/PersonDialogs';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCrmContacts } from './hooks/use-crm-contacts';
import { useTenantStore } from '../../stores/tenant.store';
import { MissingTenantView } from '../../components/common/MissingTenantView';
import { callable } from '../../lib/callable';

interface NewContactForm {
  type: 'person' | 'organization';
  firstName: string;
  lastName: string;
  name: string; // For organization name
  email: string;
  phone: string;
  organizationName: string; // Person's church/org
  types: string[]; // Added: roles
}

const DEFAULT_FORM: NewContactForm = {
  type: 'person',
  firstName: '',
  lastName: '',
  name: '',
  email: '',
  phone: '',
  organizationName: '',
  types: [],
};

type SortField = 'name' | 'type' | 'organizationName' | 'email';
type SortDir = 'asc' | 'desc';

export function CrmContactsPage() {
  // SINGULAR Y NO `['sis', 'common']`. La forma de arreglo funciona en
  // ejecución pero el guardián de claves no la sabe leer, y con ella este
  // archivo pasó de 4 claves rotas a 37 SIN QUE NINGUNA LO ESTUVIERA: el
  // detector quedaba ciego justo donde más falta hace. Está documentado en
  // CLAUDE.md como trampa conocida desde el 23-09-2026.
  //
  // Se comprobó antes de cambiarlo: ninguna de las 34 claves sin prefijo de
  // este archivo resolvía sólo en `common`. Si alguna lo necesitara, basta
  // anteponerle el nombre del namespace y dos puntos; con prefijo explícito
  // resuelve igual en la forma singular.
  //
  // (Y sin escribir aquí una llamada de ejemplo: el detector de claves lee los
  //  comentarios igual que el código, y una de muestra aparece como clave rota.
  //  Pasó al escribir esta misma explicación.)
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<NewContactForm>(DEFAULT_FORM);
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function clearFilters() {
    setSearch('');
    setFilterType('');
    setFilterRole('');
  }

  // Deletion state
  const [deletingContact, setDeletingContact] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit state
  const [editingContact, setEditingContact] = useState<any | null>(null);

  // Convert-to-Person state
  const [convertingContact, setConvertingContact] = useState<any | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  // Admission state
  const [admissionDialog, setAdmissionDialog] = useState<{
    open: boolean;
    personId?: string;
    initialData?: { firstName: string; lastName: string; email: string; phone?: string };
  }>({ open: false });

  const { data: contactsResponse, isLoading: loading } = useCrmContacts();
  const contacts = contactsResponse?.items || [];

  const hasActiveFilters = search || filterType || filterRole;

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    let result = contacts.filter((c) => {
      const matchSearch =
        !term ||
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.organizationName?.toLowerCase().includes(term) ||
        c.tags?.some((tag: string) => tag.toLowerCase().includes(term));
      const matchType = !filterType || c.type === filterType;
      const matchRole = !filterRole || c.types?.includes(filterRole);
      return matchSearch && matchType && matchRole;
    });

    result = [...result].sort((a, b) => {
      const av = (a[sortField as keyof typeof a] as string) ?? '';
      const bv = (b[sortField as keyof typeof b] as string) ?? '';
      const cmp = av.toString().localeCompare(bv.toString(), 'es', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [contacts, search, filterType, filterRole, sortField, sortDir]);

  const handleOpenDialog = (contact?: any) => {
    // Robust check: if contact is actually an Event (from onClick={handleOpenDialog}), ignore it as a contact
    const isEvent =
      contact &&
      (contact instanceof Event || (contact.nativeEvent && contact.nativeEvent instanceof Event));

    if (contact && !isEvent) {
      setEditingContact(contact);
      setForm({
        type: contact.type,
        firstName: contact.firstName || contact.name?.split(' ')[0] || '',
        lastName: contact.lastName || contact.name?.split(' ').slice(1).join(' ') || '',
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        organizationName: contact.organizationName || '',
        types: contact.types || [],
      });
      setTags(contact.tags || []);
    } else {
      setEditingContact(null);
      setForm(DEFAULT_FORM);
      setTags([]);
    }
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleFormChange = (field: keyof NewContactForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const isPerson = form.type === 'person';
    const isEditing = !!editingContact;
    const hasName = isPerson ? form.firstName.trim() && form.lastName.trim() : form.name.trim();

    if (!hasName) {
      setSaveError(isPerson ? t('crm.contacts.nameRequired') : t('crm.contacts.orgNameRequired'));
      return;
    }

    if (!currentTenantId) {
      setSaveError(t('crm.contacts.noTenant'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (isEditing) {
        const updateContact = callable('updateCrmContact');
        const payload = {
          tenantId: currentTenantId,
          contactId: editingContact.id,
          name: isPerson ? `${form.firstName.trim()} ${form.lastName.trim()}` : form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          organizationName: form.organizationName.trim(),
          tags: tags,
          types: form.types,
        };
        console.log('[CRM] Calling updateCrmContact with payload:', payload);
        await updateContact(payload);
      } else {
        const createContact = callable('createCrmContact');
        const payload: any = {
          tenantId: currentTenantId,
          type: form.type,
          email: form.email.trim(),
          phone: form.phone.trim(),
          tags: tags.length > 0 ? tags : undefined,
          types: form.types.length > 0 ? form.types : undefined,
        };

        console.log('[CRM] Calling createCrmContact with payload:', payload);

        if (isPerson) {
          payload.firstName = form.firstName.trim();
          payload.lastName = form.lastName.trim();
          if (form.organizationName.trim()) {
            payload.organizationName = form.organizationName.trim();
          }
        } else {
          payload.name = form.name.trim();
        }
        await createContact(payload);
      }

      setDialogOpen(false);
    } catch (err: any) {
      setSaveError(
        err.message ||
          t('crm.contacts.saveError', {
            accion: isEditing ? t('crm.contacts.actionUpdate') : t('crm.contacts.actionCreate'),
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingContact || !currentTenantId) return;

    setIsDeleting(true);
    try {
      const removeContact = callable('deleteCrmContact');
      await removeContact({ tenantId: currentTenantId, contactId: deletingContact.id });
      setDeletingContact(null);
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      alert(t('crm.contacts.deleteError') + ' ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConvert = async () => {
    if (!convertingContact || !currentTenantId) return;
    setIsConverting(true);
    try {
      const convert = callable('convertCrmContactToPerson');
      await convert({ tenantId: currentTenantId, contactId: convertingContact.id });
      setConvertingContact(null);
    } catch (err: any) {
      console.error('Error converting contact:', err);
      // EL RESPALDO NO SE PIERDE: al migrar cambié «Error desconocido» por una
      // cadena vacía, y sin `message` el aviso quedaba en «Error al registrar la
      // persona: » y nada más — un error que no dice nada es peor que uno feo.
      alert(
        t('crm.contacts.registerError') + ' ' + (err.message ?? t('crm.contacts.unknownError')),
      );
    } finally {
      setIsConverting(false);
    }
  };

  if (!currentTenantId) {
    return <MissingTenantView />;
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        mb={3}
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="white">
            {t('crm.contacts.pageTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? t('crm.contacts.shown', { mostrados: filtered.length, total: contacts.length })
              : t('crm.contacts.active', { count: contacts.length })}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddRounded />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
          onClick={() => handleOpenDialog()}
        >
          {t('crm.contacts.newContact')}
        </Button>
      </Stack>

      {/* ── Search + Filters bar ───────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        mb={2}
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <TextField
          placeholder={t('crm.contacts.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: 'action.active' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>
            <FilterListRounded sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            {t('crm.contacts.type')}
          </InputLabel>
          <Select
            value={filterType}
            label={t('crm.contacts.type')}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="">{t('crm.contacts.all')}</MenuItem>
            <MenuItem value="person">{t('crm.contacts.person')}</MenuItem>
            <MenuItem value="organization">{t('crm.contacts.organization')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('crm.contacts.roleOrStatus')}</InputLabel>
          <Select
            value={filterRole}
            label={t('crm.contacts.roleOrStatus')}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <MenuItem value="">{t('crm.contacts.allRoles')}</MenuItem>
            {/* El rótulo sale de la clave y el `value` NO se toca: traducirlo
                rompería el filtro sin que nada fallara al compilar. */}
            {(
              [
                'externo',
                'prospecto-académico',
                'student',
                'alumni',
                'faculty',
                'staff',
                'contact',
              ] as const
            ).map((v) => (
              <MenuItem key={v} value={v}>
                {t(`crm.contacts.${v}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Tooltip title={t('crm.contacts.clearFilters')}>
            {/* EL RÓTULO VA EN EL BOTÓN. El tooltip es ayuda VISUAL: quien
                navega con teclado o lector de pantalla no lo recibe, y un botón
                que sólo tiene un icono se anuncia como «botón» a secas. */}
            <IconButton
              size="small"
              aria-label={t('crm.contacts.clearFilters')}
              onClick={clearFilters}
              sx={{ mt: 0.5 }}
            >
              <ClearRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                <TableCell sortDirection={sortField === 'name' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    {t('people.columns.name', 'Nombre')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'type' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'type'}
                    direction={sortField === 'type' ? sortDir : 'asc'}
                    onClick={() => handleSort('type')}
                  >
                    {t('crm.contacts.type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'email' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'email'}
                    direction={sortField === 'email' ? sortDir : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    {t('people.columns.email', 'Email / Tel')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'organizationName' ? sortDir : false}>
                  <TableSortLabel
                    active={sortField === 'organizationName'}
                    direction={sortField === 'organizationName' ? sortDir : 'asc'}
                    onClick={() => handleSort('organizationName')}
                  >
                    {t('crm.contacts.organization')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t('crm.contacts.roleOrStatus')}</TableCell>
                <TableCell>{t('people.columns.tags', 'Etiquetas')}</TableCell>
                <TableCell align="right">{t('people.columns.actions', 'Acciones')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 6 }}>
                    {t('crm.contacts.noneFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    onClick={() => {
                      if (contact.personId) {
                        navigate(`/people/${contact.personId}`);
                      }
                    }}
                    sx={{
                      cursor: contact.personId ? 'pointer' : 'default',
                      '& td': { color: 'text.primary' },
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{contact.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          contact.type === 'organization'
                            ? t('crm.contacts.organization')
                            : t('crm.contacts.person')
                        }
                        color={contact.type === 'organization' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      <Typography variant="body2">{contact.email || '--'}</Typography>
                      <Typography variant="caption">{contact.phone}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {contact.organizationName || '--'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {contact.types?.map((type: string) => (
                          <Chip
                            key={type}
                            label={t(`people.types.${type}`)}
                            size="small"
                            color={TYPE_COLORS[type] || 'default'}
                            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                          />
                        ))}
                        {!contact.types?.length && contact.type === 'person' && (
                          <Typography variant="caption" color="text.disabled">
                            {t('crm.contacts.noRole')}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {contact.tags?.slice(0, 3).map((tag: string) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ color: 'text.secondary' }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end">
                        <Tooltip title={t('crm.contacts.viewProfile')}>
                          <IconButton
                            aria-label={t('crm.contacts.viewProfile')}
                            size="small"
                            sx={{ color: 'primary.main' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (contact.personId) navigate(`/people/${contact.personId}`);
                            }}
                            disabled={!contact.personId}
                          >
                            <VisibilityRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('crm.contacts.edit')}>
                          <IconButton
                            aria-label={t('crm.contacts.edit')}
                            size="small"
                            sx={{ color: 'action.active' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(contact);
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('crm.contacts.delete')}>
                          <IconButton
                            aria-label={t('crm.contacts.delete')}
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingContact(contact);
                            }}
                          >
                            <DeleteRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {contact.type === 'person' && !contact.personId && (
                          <Tooltip title={t('crm.contacts.registerPerson')}>
                            <IconButton
                              aria-label={t('crm.contacts.registerPerson')}
                              size="small"
                              color="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConvertingContact(contact);
                              }}
                            >
                              <PersonAddRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {contact.type === 'person' && (
                          <Tooltip title={t('crm.contacts.createAdmission')}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAdmissionDialog({
                                  open: true,
                                  personId: contact.personId,
                                  initialData: {
                                    firstName:
                                      contact.firstName || contact.name?.split(' ')[0] || '',
                                    lastName:
                                      contact.lastName ||
                                      contact.name?.split(' ').slice(1).join(' ') ||
                                      '',
                                    email: contact.email || '',
                                    phone: contact.phone || '',
                                  },
                                });
                              }}
                            >
                              <HistoryEduRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Create/Edit Contact Dialog ────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingContact ? t('crm.contacts.editTitle') : t('crm.contacts.newTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} pt={1}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <FormControl fullWidth required>
              <InputLabel>{t('crm.contacts.type')}</InputLabel>
              <Select
                value={form.type}
                label={t('crm.contacts.type')}
                onChange={(e) => handleFormChange('type', e.target.value)}
              >
                <MenuItem value="person">{t('crm.contacts.person')}</MenuItem>
                <MenuItem value="organization">{t('crm.contacts.organization')}</MenuItem>
              </Select>
            </FormControl>

            {form.type === 'person' ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t('crm.contacts.firstName')}
                  value={form.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label={t('crm.contacts.lastName')}
                  value={form.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  fullWidth
                  required
                />
              </Stack>
            ) : (
              <TextField
                label={t('crm.contacts.orgName')}
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                fullWidth
                required
                autoFocus
              />
            )}

            <TextField
              label={t('crm.contacts.email')}
              type="email"
              value={form.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              fullWidth
            />

            <TextField
              label={t('crm.contacts.phone')}
              value={form.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              fullWidth
            />

            {form.type === 'person' && (
              <TextField
                label={t('crm.contacts.orgOrChurch')}
                value={form.organizationName}
                onChange={(e) => handleFormChange('organizationName', e.target.value)}
                fullWidth
                helperText={t('crm.contacts.orgOptional')}
              />
            )}

            <FormControl fullWidth required>
              <InputLabel>{t('crm.contacts.rolesTypes')}</InputLabel>
              <Select
                multiple
                value={form.types}
                onChange={(e) =>
                  handleFormChange(
                    'types',
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : (e.target.value as any),
                  )
                }
                label={t('crm.contacts.rolesTypes')}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={t(`people.types.${value}`, { defaultValue: value, ns: 'sis' })}
                        size="small"
                        color={TYPE_COLORS[value] || 'default'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem
                  disabled
                  sx={{
                    fontWeight: 700,
                    opacity: '1 !important',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    mt: 1,
                  }}
                >
                  {t('crm.contacts.lifecycle')}
                </MenuItem>
                {['externo', 'prospecto-académico', 'student', 'alumni'].map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`people.types.${type}`, { defaultValue: type, ns: 'sis' })}
                  </MenuItem>
                ))}

                <MenuItem
                  disabled
                  sx={{
                    fontWeight: 700,
                    opacity: '1 !important',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    mt: 2,
                  }}
                >
                  {t('crm.contacts.institutionalRoles')}
                </MenuItem>
                {['faculty', 'staff', 'contact'].map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`people.types.${type}`, { defaultValue: type, ns: 'sis' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              freeSolo
              options={['donante', 'pastor', 'iglesia-aliada', 'voluntario', 'misionero']}
              value={tags}
              onChange={(_, newValue) => setTags(newValue as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...(params as any)}
                  label={t('crm.contacts.tagsSection')}
                  placeholder={t('crm.contacts.tagsPlaceholder')}
                  helperText={t('crm.contacts.tagsHelp')}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('crm.contacts.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving ||
              (form.type === 'person'
                ? !form.firstName.trim() || !form.lastName.trim()
                : !form.name.trim())
            }
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving
              ? t('crm.contacts.saving')
              : editingContact
                ? t('crm.contacts.update')
                : t('crm.contacts.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Convert to Person Confirmation Dialog ────────────────────────── */}
      <Dialog open={!!convertingContact} onClose={() => setConvertingContact(null)}>
        <DialogTitle>{t('crm.contacts.registerTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {/* EL NOMBRE VA DENTRO DE LA TRADUCCIÓN: en otro idioma puede caer
                en otra posición de la frase, y cosiendo dos mitades se impone un
                orden que no todos los idiomas admiten. */}
            <Trans
              i18nKey="crm.contacts.registerAsk"
              ns="sis"
              values={{ nombre: convertingContact?.name ?? '' }}
              components={{ b: <strong /> }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('crm.contacts.registerHelp')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConvertingContact(null)} disabled={isConverting}>
            {t('crm.contacts.cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConvert}
            disabled={isConverting}
            startIcon={
              isConverting ? <CircularProgress size={16} color="inherit" /> : <PersonAddRounded />
            }
          >
            {isConverting ? t('crm.contacts.registering') : t('crm.contacts.confirmRegister')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Confirmation Dialog ────────────────────────────────────── */}
      <Dialog open={!!deletingContact} onClose={() => setDeletingContact(null)}>
        <DialogTitle>{t('crm.contacts.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              i18nKey="crm.contacts.deleteAsk"
              ns="sis"
              values={{ nombre: deletingContact?.name ?? '' }}
              components={{ b: <strong /> }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('crm.contacts.deleteHelp')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeletingContact(null)} disabled={isDeleting}>
            {t('crm.contacts.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isDeleting ? t('crm.contacts.deleting') : t('crm.contacts.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateAdmissionDialog
        open={admissionDialog.open}
        onClose={() => setAdmissionDialog({ open: false })}
        personId={admissionDialog.personId}
        initialData={admissionDialog.initialData}
      />
    </Box>
  );
}
